import {
  Alert, Anchor, Badge, Breadcrumbs, Button, Divider, FileInput, Grid, Group, List, Modal, Paper, Progress, ScrollArea, SegmentedControl,
  SimpleGrid, Skeleton, Stack, Switch, Table, Text, Textarea, TextInput, ThemeIcon, Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconAlertTriangle, IconArrowBackUp, IconBuildingCommunity, IconCheck, IconCircleCheck, IconCircleX, IconClipboardCheck,
  IconExternalLink, IconFile, IconHistory, IconLock, IconMail, IconMapPin, IconPhone, IconScan, IconSend, IconTrees, IconUpload, IconUserCheck,
  IconUserPlus, IconX,
} from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { RugippSearch } from "../../components/RugippSearch";

import { http } from "../../api/http";
import { useCan } from "../../auth/user";
import { fmtDate, fmtDateTime, fmtNum } from "../../components/ui";
import { UnitHero } from "../../components/UnitHero";
import { useT } from "../../i18n/script";
import { speciesColor, useSpeciesNames } from "../holdings/HoldingsPage";
import {
  AssignModal, type CaseDetail, CaseStatusBadge, CasesStyle, CaseTimeline, type CheckState, DueText, errMsg, fmtArea, fmtSize,
  HOLDING_TYPE, OPEN_STATUSES, openDocument, PARCEL_USE, ParcelMap, RELATION, Section,
} from "./common";
import { ScannedAttachments } from "./ScanWizard";

/** Detalj predmeta (Agencija): poređenje sa Registrom i katastrom, lista provjera, odluka, istorija i poruke. */
export default function CaseDetailPage() {
  const { id } = useParams();
  const { t } = useT();
  const nav = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["case", id], queryFn: () => http.get<CaseDetail>(`/api/v1/cases/${id}`), retry: false });
  const can = useCan();
  const [assignOpen, setAssignOpen] = useState(false);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["case", id] });
    qc.invalidateQueries({ queryKey: ["cases"] });
    qc.invalidateQueries({ queryKey: ["cases-stats"] });
  };

  if (q.error) return <Alert color="red" icon={<IconAlertTriangle />} title={t("Predmet nije dostupan")}>{(q.error as Error).message}</Alert>;
  if (!q.data) return <Stack><Skeleton h={170} radius="lg" /><Grid><Grid.Col span={{ base: 12, lg: 8 }}><Skeleton h={400} radius="lg" /></Grid.Col><Grid.Col span={{ base: 12, lg: 4 }}><Skeleton h={400} radius="lg" /></Grid.Col></Grid></Stack>;
  const c = q.data;
  const d = c.data ?? ({} as CaseDetail["data"]);
  const reg = c.registry;
  const found = (reg?.parcels ?? []).filter((p) => p.found && p.cadastre?.geojson);
  const area = found.reduce((s, p) => s + (p.cadastre?.area_m2 ?? 0), 0);
  const open = OPEN_STATUSES.includes(c.status);
  const heroGeo = found.length ? { type: "FeatureCollection", features: found.map((p) => ({ type: "Feature", geometry: p.cadastre!.geojson, properties: {} })) } : undefined;

  return (
    <Stack gap="md">
      <CasesStyle />
      <Breadcrumbs>
        <Anchor size="sm" onClick={() => nav("/predmeti")}>{t("Predmeti")}</Anchor>
        <Text size="sm">{c.number}</Text>
      </Breadcrumbs>

      <UnitHero geojson={heroGeo}
        subtitle={<>{c.number} · {t(c.kind_label ?? c.kind)}</>}
        title={d.holding_name || c.applicant_name || c.number}
        badges={<>
          <CaseStatusBadge status={c.status} variant="filled" long size="md" />
          {c.overdue && <Badge color="red" variant="filled" size="md" leftSection={<IconAlertTriangle size={12} />}>{t("Preko roka")}</Badge>}
          {c.bpg && <Badge variant="white" color="dark" size="md">BPG {c.bpg}</Badge>}
          {c.opstina && <Badge variant="white" color="agri" size="md" leftSection={<IconMapPin size={12} />}>{c.opstina}</Badge>}
        </>}
        stats={[
          { label: t("čestica"), value: `${found.length}/${d.parcels?.length ?? 0}` },
          { label: t("površina"), value: area ? fmtArea(area) : "—" },
          { label: t("voćnjaka"), value: d.orchards?.length ?? 0 },
          { label: t("priloga"), value: c.documents.length },
        ]} />

      <Paper withBorder radius="lg" p="md">
        <SimpleGrid cols={{ base: 1, xs: 2, md: 4 }} spacing="md">
          <Info label="Podnosilac" icon={<IconUserCheck size={15} />}>
            <Text fw={700} size="sm">{c.applicant_name ?? "—"}</Text>
            <Text size="xs" c="dimmed">{t(RELATION[d.relation] ?? d.relation ?? "")}</Text>
          </Info>
          <Info label="Kontakt" icon={<IconMail size={15} />}>
            {c.applicant_email ? <Anchor size="sm" href={`mailto:${c.applicant_email}`}>{c.applicant_email}</Anchor> : <Text size="sm">—</Text>}
            {c.applicant_phone && <Group gap={4}><IconPhone size={12} /><Anchor size="xs" href={`tel:${c.applicant_phone}`}>{c.applicant_phone}</Anchor></Group>}
          </Info>
          <Info label="Referent" icon={<IconUserPlus size={15} />}>
            <Text size="sm" fw={600}>{c.assigned_name ?? t("nije dodijeljen")}</Text>
            {c.assigned_at && <Text size="xs" c="dimmed">{t("od")} {fmtDate(c.assigned_at)}</Text>}
          </Info>
          <Info label="Rok / podneseno" icon={<IconHistory size={15} />}>
            <DueText due={c.due_date} overdue={c.overdue} closed={!open} />
            <Text size="xs" c="dimmed">{t("Podneseno")} {fmtDateTime(c.submitted_at)}</Text>
          </Info>
        </SimpleGrid>
      </Paper>

      <Grid gap="md">
        <Grid.Col span={{ base: 12, lg: 8 }}>
          <Stack gap="md">
            <HoldingCompare c={c} />
            <ParcelsCard c={c} />
            <OrchardsCard c={c} />
            <DocsCard c={c} onChange={refresh} />
            <Section title="Skeniranje i raspoređivanje priloga" icon={<IconScan size={17} />}>
              <ScannedAttachments caseId={c.id} caseNumber={c.number} holdingId={c.holding_id ?? null}
                canScan={can("cases:process") || can("dms:write")} canClassify={can("cases:process") || can("dms:write")} />
            </Section>
          </Stack>
        </Grid.Col>
        <Grid.Col span={{ base: 12, lg: 4 }}>
          <div className="case-sticky">
            <ScrollArea.Autosize mah="calc(100dvh - var(--header-h) - 24px)" type="auto" offsetScrollbars scrollbars="y">
              <Stack gap="md">
                <ActionsCard c={c} onAssign={() => setAssignOpen(true)} onChange={refresh} />
                <Section title="Istorija i poruke" icon={<IconHistory size={17} />}>
                  <CommentBox caseId={c.id} onSent={refresh} />
                  <Divider my="md" />
                  <CaseTimeline events={c.events} />
                </Section>
              </Stack>
            </ScrollArea.Autosize>
          </div>
        </Grid.Col>
      </Grid>

      <AssignModal opened={assignOpen} caseId={c.id} caseNumber={c.number} current={c.assigned_to} onClose={() => setAssignOpen(false)} onDone={refresh} />
    </Stack>
  );
}

function Info({ label, icon, children }: { label: string; icon?: ReactNode; children: ReactNode }) {
  const { t } = useT();
  return (
    <Group gap={10} align="flex-start" wrap="nowrap">
      <ThemeIcon variant="light" color="agri" radius="xl" size={30}>{icon}</ThemeIcon>
      <div style={{ minWidth: 0 }}>
        <Text size="xs" c="dimmed" tt="uppercase" fw={700}>{t(label)}</Text>
        {children}
      </div>
    </Group>
  );
}

// ============================================================================== podaci gazdinstva
function Mark({ ok, hint }: { ok: boolean | null | undefined; hint?: string }) {
  const { t } = useT();
  if (ok == null) return <Text size="xs" c="dimmed">—</Text>;
  return (
    <Tooltip label={t(hint ?? (ok ? "Odgovara Registru" : "Ne odgovara Registru"))} withArrow>
      <ThemeIcon size={22} radius="xl" color={ok ? "green" : "orange"} variant="light">{ok ? <IconCheck size={14} /> : <IconAlertTriangle size={14} />}</ThemeIcon>
    </Tooltip>
  );
}

const norm = (s?: string | null) => (s ?? "").trim().toLowerCase();

function HoldingCompare({ c }: { c: CaseDetail }) {
  const { t } = useT();
  const d = c.data;
  const h = c.registry?.holding;
  const rows: { label: string; mine: ReactNode; reg?: ReactNode; ok?: boolean | null }[] = [
    { label: "BPG", mine: d.bpg ?? "—", reg: h?.bpg, ok: h ? true : d.bpg ? false : null },
    { label: "Naziv gazdinstva", mine: d.holding_name ?? "—", reg: h?.name, ok: h ? norm(h.name) === norm(d.holding_name) : null },
    { label: "Nosilac", mine: d.holder_name ?? "—", reg: h?.holder_name, ok: h ? !!c.registry?.holder_match : null },
    { label: "Opština", mine: c.opstina ?? "—", reg: h?.opstina, ok: h ? norm(h.opstina) === norm(c.opstina) : null },
    { label: "RPG status", mine: <Text size="sm" c="dimmed">—</Text>, reg: h?.rpg_status, ok: h ? h.rpg_status === "aktivan" : null },
    { label: "Verifikacija", mine: <Text size="sm" c="dimmed">—</Text>, reg: h?.verification, ok: null },
  ];
  const extra: [string, ReactNode][] = [
    ["Vrsta lica", t(HOLDING_TYPE[d.holding_type] ?? d.holding_type)], ...(d.jib ? [["JIB", d.jib] as [string, ReactNode]] : []),
    ["Odnos prema gazdinstvu", t(RELATION[d.relation] ?? d.relation)], ["Adresa", d.address ?? "—"], ["Telefon", d.phone ?? "—"],
    ["E-pošta", d.email ?? "—"],
  ];
  return (
    <Section title="Podaci gazdinstva" icon={<IconBuildingCommunity size={17} />}
      right={h ? <Badge color={c.registry?.holder_match ? "green" : "orange"} variant="light">{t(c.registry?.holder_match ? "Nosilac odgovara" : "Provjeriti nosioca")}</Badge>
        : <Badge color="blue" variant="light">{t("Novo gazdinstvo")}</Badge>}>
      {!h && (
        <Alert color="blue" variant="light" mb="sm" icon={<IconAlertTriangle size={16} />}>
          {t(d.bpg ? `BPG ${d.bpg} ne postoji u Registru u sistemu — odobrenjem se kreira novo gazdinstvo (izvor: samoregistracija). Provjerite rješenje o upisu u RPG.`
            : "Podnosilac nije unio BPG — predmet se ne može odobriti bez BPG.")}
        </Alert>
      )}
      <Table withRowBorders verticalSpacing={6} fz="sm" layout="fixed">
        <Table.Thead><Table.Tr>
          <Table.Th w="26%">{t("Podatak")}</Table.Th><Table.Th>{t("Uneseno")}</Table.Th><Table.Th>{t("Registar")}</Table.Th><Table.Th w={46} />
        </Table.Tr></Table.Thead>
        <Table.Tbody>
          {rows.map((r) => (
            <Table.Tr key={r.label} style={r.ok === false ? { background: "rgba(253,126,20,.07)" } : undefined}>
              <Table.Td c="dimmed">{t(r.label)}</Table.Td>
              <Table.Td fw={600}>{r.mine}</Table.Td>
              <Table.Td>{h ? (r.reg ?? "—") : <Text size="sm" c="dimmed">{t("nema u Registru")}</Text>}</Table.Td>
              <Table.Td><Mark ok={r.ok} /></Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="xs" mt="md">
        {extra.map(([k, v]) => (
          <div key={k}><Text size="xs" c="dimmed">{t(k)}</Text><Text size="sm" fw={600} style={{ wordBreak: "break-word" }}>{v}</Text></div>
        ))}
      </SimpleGrid>
      {d.message && (
        <Alert mt="md" variant="light" color="gray" title={t("Poruka podnosioca")}><Text size="sm" style={{ whiteSpace: "pre-wrap" }}>{d.message}</Text></Alert>
      )}
    </Section>
  );
}

// ============================================================================== čestice
/** Druga gazdinstva koja već koriste česticu (bez gazdinstva iz samog predmeta, npr. nakon odobrenja). */
function otherUse(koristi: string | null | undefined, bpg?: string | null): string | null {
  if (!koristi) return null;
  const rest = koristi.split("; ").filter((x) => !bpg || !x.startsWith(`${bpg} `));
  return rest.length ? rest.join("; ") : null;
}

function ParcelsCard({ c }: { c: CaseDetail }) {
  const { t } = useT();
  const bpg = c.data.bpg ?? c.bpg;
  const ps = (c.registry?.parcels ?? (c.data.parcels ?? []).map((p) => ({ ...p, found: false, cadastre: null })))
    .map((p) => ({ ...p, cadastre: p.cadastre ? { ...p.cadastre, koristi: otherUse(p.cadastre.koristi, bpg) } : null }));
  const nf = ps.filter((p) => !p.found).length;
  const used = ps.filter((p) => p.cadastre?.koristi).length;
  const items = ps.filter((p) => p.cadastre?.geojson).map((p) => ({ geojson: p.cadastre!.geojson, label: p.number, warn: !!p.cadastre?.koristi }));
  const [rg, setRg] = useState(false);
  const first = String(ps[0]?.number ?? "").split("/");
  return (
    <Section title="Katastarske čestice" icon={<IconMapPin size={17} />}
      right={<Group gap={6}>
        <Button size="compact-xs" variant="light" onClick={() => setRg(true)}>{t("RUGIPP katastar")}</Button>
        <RugippSearch opened={rg} onClose={() => setRg(false)} caseNumber={c.number} initial={{ broj: first[0], podbroj: first[1] ?? "0" }} />
        <Badge variant="light" color={nf ? "orange" : "green"}>{t(nf ? `${nf} nije u katastru` : "Sve u katastru")}</Badge>
        {used > 0 && <Badge variant="light" color="red">{t(`${used} već koristi drugo gazdinstvo`)}</Badge>}
      </Group>}>
      {!ps.length ? <Text size="sm" c="dimmed">{t("Nema unesenih čestica")}</Text> : (
        <Grid gap="md">
          <Grid.Col span={{ base: 12, md: items.length ? 7 : 12 }}>
            <ScrollArea type="auto" scrollbars="x">
              <Table verticalSpacing={6} fz="sm" miw={460}>
                <Table.Thead><Table.Tr>
                  <Table.Th>KO</Table.Th><Table.Th>KČ</Table.Th><Table.Th>{t("Katastar")}</Table.Th>
                  <Table.Th ta="right">{t("Površina")}</Table.Th><Table.Th>{t("Kultura")}</Table.Th>
                </Table.Tr></Table.Thead>
                <Table.Tbody>
                  {ps.map((p, i) => (
                    <Table.Tr key={`${p.ko_id}-${p.number}-${i}`}>
                      <Table.Td>{p.ko_name ?? "—"}</Table.Td>
                      <Table.Td fw={700} ff="monospace">{p.number}</Table.Td>
                      <Table.Td>
                        <Group gap={4} wrap="nowrap">
                          {p.found ? <IconCircleCheck size={17} color="var(--mantine-color-green-7)" /> : <IconCircleX size={17} color="var(--mantine-color-orange-7)" />}
                          {p.cadastre?.koristi && (
                            <Tooltip label={`${t("Već na voćnjaku gazdinstva")}: ${p.cadastre.koristi}`} multiline w={260} withArrow>
                              <Badge size="xs" color="red" variant="light" leftSection={<IconAlertTriangle size={10} />}>{t("koristi se")}</Badge>
                            </Tooltip>
                          )}
                        </Group>
                      </Table.Td>
                      <Table.Td ta="right">{fmtArea(p.cadastre?.area_m2)}</Table.Td>
                      <Table.Td>
                        <Text size="sm">{p.cadastre?.land_use ?? "—"}</Text>
                        {p.use && <Text size="xs" c="dimmed">{t("prijavljeno")}: {t(PARCEL_USE[p.use] ?? p.use)}</Text>}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Grid.Col>
          {items.length > 0 && <Grid.Col span={{ base: 12, md: 5 }}><ParcelMap items={items} height={280} /></Grid.Col>}
        </Grid>
      )}
    </Section>
  );
}

// ============================================================================== voćnjaci
function OrchardsCard({ c }: { c: CaseDetail }) {
  const { t } = useT();
  const sp = useSpeciesNames();
  const os = c.data.orchards ?? [];
  const foundNums = new Set((c.registry?.parcels ?? []).filter((p) => p.found).map((p) => String(p.number)));
  return (
    <Section title="Prijavljeni voćnjaci" icon={<IconTrees size={17} />} right={<Badge variant="light">{os.length}</Badge>}>
      {!os.length ? <Text size="sm" c="dimmed">{t("Podnosilac nije prijavio voćnjake")}</Text> : (
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          {os.map((o, i) => {
            const ok = o.parcels.filter((n) => foundNums.has(String(n))).length;
            return (
              <Paper key={i} withBorder radius="md" p="sm" style={{ borderLeft: `5px solid ${speciesColor(o.species)}` }}>
                <Group justify="space-between" wrap="nowrap" align="flex-start">
                  <div style={{ minWidth: 0 }}>
                    <Text fw={750} truncate>{o.name || `${t(sp(o.species))}${o.cultivar ? ` — ${o.cultivar}` : ""}`}</Text>
                    <Text size="xs" c="dimmed">{t(sp(o.species))}{o.cultivar ? `, ${o.cultivar}` : ""}{o.rootstock ? ` / ${o.rootstock}` : ""}{o.planting_year ? ` · ${o.planting_year}.` : ""}</Text>
                  </div>
                  <Group gap={4}>
                    {o.organic && <Badge size="xs" color="lime">{t("organski")}</Badge>}
                    {o.hail_net && <Badge size="xs" color="gray" variant="light">{t("protivgradna mreža")}</Badge>}
                  </Group>
                </Group>
                <Group gap="lg" mt="xs">
                  <div><Text fw={750}>{fmtNum(o.trees, 0)}</Text><Text size="xs" c="dimmed">{t("stabala")}</Text></div>
                  <div><Text fw={750}>{o.row_spacing_m && o.tree_spacing_m ? `${fmtNum(o.row_spacing_m, 2)} × ${fmtNum(o.tree_spacing_m, 2)}` : "—"}</Text><Text size="xs" c="dimmed">{t("razmak, m")}</Text></div>
                  {o.irrigation && <div><Text fw={750}>{t(o.irrigation)}</Text><Text size="xs" c="dimmed">{t("navodnjavanje")}</Text></div>}
                </Group>
                <Group gap={4} mt="xs">
                  <Text size="xs" c="dimmed">KČ:</Text>
                  {o.parcels.length ? o.parcels.map((n) => (
                    <Badge key={n} size="xs" variant="light" color={foundNums.has(String(n)) ? "green" : "orange"}>{n}</Badge>
                  )) : <Text size="xs" c="orange.7">{t("bez čestica — neće biti kreiran")}</Text>}
                </Group>
                {o.parcels.length > 0 && ok === 0 && <Text size="xs" c="orange.7" mt={4}>{t("Nijedna čestica nije u katastru — voćnjak neće biti kreiran")}</Text>}
              </Paper>
            );
          })}
        </SimpleGrid>
      )}
    </Section>
  );
}

// ============================================================================== prilozi
function DocsCard({ c, onChange }: { c: CaseDetail; onChange: () => void }) {
  const { t } = useT();
  const can = useCan();
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", `${c.number} — ${file.name}`);
      await http.form(`/api/v1/cases/${c.id}/documents`, fd);
      setFile(null); onChange();
      notifications.show({ color: "green", message: t("Prilog je dodat") });
    } catch (e) { notifications.show({ color: "red", message: errMsg(e) }); } finally { setBusy(false); }
  };
  return (
    <Section title="Prilozi" icon={<IconFile size={17} />} right={<Badge variant="light">{c.documents.length}</Badge>}>
      {!c.documents.length ? <Text size="sm" c="dimmed" mb="sm">{t("Nema priloženih dokumenata")}</Text> : (
        <Stack gap={6} mb="sm">
          {c.documents.map((doc) => (
            <Paper key={doc.id} withBorder radius="md" p="xs" className="holding-card" onClick={() => openDocument(c.id, doc, (m) => notifications.show({ color: "red", message: m }))}>
              <Group justify="space-between" wrap="nowrap">
                <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                  <ThemeIcon variant="light" color="soil" radius="md"><IconFile size={16} /></ThemeIcon>
                  <div style={{ minWidth: 0 }}>
                    <Text size="sm" fw={600} truncate>{doc.title}</Text>
                    <Text size="xs" c="dimmed" truncate>{doc.filename} · {fmtSize(doc.size_bytes)} · {doc.reg_no} · {fmtDateTime(doc.created_at)} · {doc.created_by}</Text>
                  </div>
                </Group>
                <IconExternalLink size={16} color="var(--mantine-color-dimmed)" />
              </Group>
            </Paper>
          ))}
        </Stack>
      )}
      {can("cases:process") && OPEN_STATUSES.includes(c.status) && (
        <Group gap="xs" align="flex-end">
          <FileInput size="xs" placeholder={t("Dodaj prilog (zapisnik, izvod iz RPG...)")} value={file} onChange={setFile} clearable style={{ flex: 1 }} leftSection={<IconUpload size={14} />} />
          <Button size="xs" disabled={!file} loading={busy} onClick={upload}>{t("Priloži")}</Button>
        </Group>
      )}
    </Section>
  );
}

// ============================================================================== akcije
function ActionsCard({ c, onAssign, onChange }: { c: CaseDetail; onAssign: () => void; onChange: () => void }) {
  const { t } = useT();
  const nav = useNavigate();
  const [dlg, setDlg] = useState<null | "return" | "reject" | "approve">(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [approved, setApproved] = useState<{ holding_id: number; orchards: number[] } | null>(null);
  const items = c.checklist_items ?? [];
  const cl = c.checklist ?? {};
  const okCount = items.filter((i) => cl[i]?.ok === true).length;
  const badCount = items.filter((i) => cl[i]?.ok === false).length;
  const pct = items.length ? Math.round((okCount / items.length) * 100) : 0;
  const reg = c.registry;
  const foundNums = new Set((reg?.parcels ?? []).filter((p) => p.found).map((p) => String(p.number)));
  const orchardsToCreate = (c.data.orchards ?? []).filter((o) => o.parcels.some((n) => foundNums.has(String(n)))).length;
  const open = OPEN_STATUSES.includes(c.status);

  const decide = async (kind: "return" | "reject" | "approve") => {
    setBusy(true);
    try {
      const r = await http.post<CaseDetail & { holding_id?: number; orchards?: number[] }>(`/api/v1/cases/${c.id}/${kind}`, { note: note.trim() || null });
      setDlg(null); setNote("");
      if (kind === "approve" && r.holding_id) setApproved({ holding_id: r.holding_id, orchards: r.orchards ?? [] });
      notifications.show({ color: kind === "reject" ? "red" : kind === "return" ? "orange" : "green", icon: <IconCheck size={16} />,
        message: t(kind === "approve" ? "Predmet je odobren — gazdinstvo je verifikovano" : kind === "reject" ? "Predmet je odbijen" : "Predmet je vraćen podnosiocu na dopunu") });
      onChange();
    } catch (e) {
      notifications.show({ color: "red", message: errMsg(e) });
    } finally { setBusy(false); }
  };

  return (
    <Paper withBorder radius="lg" p="md" className="case-fadein" style={{ borderTop: "4px solid var(--mantine-color-agri-7)" }}>
      <Group justify="space-between" mb="sm">
        <Group gap={8}><ThemeIcon variant="light" radius="md" size={30}><IconClipboardCheck size={17} /></ThemeIcon><Text fw={750}>{t("Obrada predmeta")}</Text></Group>
        <CaseStatusBadge status={c.status} />
      </Group>

      {(approved || (c.status === "odobren" && c.holding_id)) && (
        <Alert color="green" variant="light" icon={<IconCircleCheck />} title={t("Gazdinstvo je verifikovano")} mb="sm">
          <Text size="sm">{approved ? t(`Kreirano voćnjaka: ${approved.orchards.length}. Podnosilac je dobio pristup.`) : c.decision_note}</Text>
          <Button mt="xs" size="xs" rightSection={<IconExternalLink size={14} />} onClick={() => nav(`/gazdinstva/${approved?.holding_id ?? c.holding_id}`)}>
            {t("Otvori gazdinstvo")}
          </Button>
        </Alert>
      )}
      {c.status === "odbijen" && (
        <Alert color="red" variant="light" icon={<IconCircleX />} title={t("Odbijeno")} mb="sm">
          <Text size="sm">{c.decision_note}</Text><Text size="xs" c="dimmed" mt={4}>{c.decided_by} · {fmtDateTime(c.decided_at)}</Text>
        </Alert>
      )}
      {c.status === "dopuna" && (
        <Alert color="orange" variant="light" icon={<IconArrowBackUp />} mb="sm">{t("Čeka se dopuna od podnosioca. Biće vam ponovo dostupan za obradu kada je pošalje.")}</Alert>
      )}

      {c.can_assign && (
        <Group justify="space-between" wrap="nowrap" p="xs" mb="sm" style={{ background: "var(--mantine-color-default-hover)", borderRadius: 10 }}>
          <div style={{ minWidth: 0 }}>
            <Text size="xs" c="dimmed">{t("Referent")}</Text>
            <Text size="sm" fw={700} truncate>{c.assigned_name ?? t("nije dodijeljen")}</Text>
          </div>
          <Button size="xs" variant={c.assigned_to ? "default" : "filled"} leftSection={<IconUserPlus size={14} />} onClick={onAssign}>
            {t(c.assigned_to ? "Preraspodijeli" : "Dodijeli")}
          </Button>
        </Group>
      )}
      {!c.can_process && open && c.status !== "dopuna" && !c.can_assign && (
        <Text size="sm" c="dimmed">{t("Predmet obrađuje dodijeljeni referent.")}</Text>
      )}
      {c.status === "podnesen" && !c.can_process && (
        <Text size="sm" c="dimmed">{t("Provjera počinje kada se predmet dodijeli referentu.")}</Text>
      )}

      {(c.can_process || (items.length > 0 && Object.keys(cl).length > 0)) && (
        <>
          <Group justify="space-between" mt="xs" mb={4}>
            <Text size="sm" fw={700}>{t("Lista provjera")}</Text>
            <Text size="xs" c="dimmed">{okCount}/{items.length} {t("u redu")}{badCount ? ` · ${badCount} ${t("nije")}` : ""}</Text>
          </Group>
          <Progress.Root size="md" radius="xl" mb="sm">
            <Progress.Section value={pct} color="green" />
            <Progress.Section value={items.length ? (badCount / items.length) * 100 : 0} color="orange" />
          </Progress.Root>
          <Stack gap={6}>
            {items.map((it) => <CheckItem key={it} caseId={c.id} item={it} state={cl[it]} disabled={!c.can_process} onSaved={onChange} />)}
          </Stack>
        </>
      )}

      {c.can_process && (
        <Stack gap={8} mt="md">
          <Button fullWidth color="agri" leftSection={<IconCircleCheck size={17} />} onClick={() => { setNote(""); setDlg("approve"); }}
            disabled={!c.bpg && !c.data.bpg}>
            {t("Odobri i aktiviraj gazdinstvo")}
          </Button>
          <Group grow gap={8}>
            <Button variant="light" color="orange" leftSection={<IconArrowBackUp size={16} />} onClick={() => { setNote(""); setDlg("return"); }}>{t("Vrati na dopunu")}</Button>
            <Button variant="light" color="red" leftSection={<IconX size={16} />} onClick={() => { setNote(""); setDlg("reject"); }}>{t("Odbij")}</Button>
          </Group>
          {badCount > 0 && <Text size="xs" c="orange.7">{t("Neke provjere nisu prošle — razmotrite vraćanje na dopunu.")}</Text>}
        </Stack>
      )}

      <Modal opened={dlg === "return" || dlg === "reject"} onClose={() => setDlg(null)} centered radius="lg"
        title={<Text fw={750}>{t(dlg === "reject" ? "Odbijanje zahtjeva" : "Vraćanje na dopunu")}</Text>}>
        <Stack gap="sm">
          <Text size="sm" c="dimmed">{t(dlg === "reject"
            ? "Podnosilac dobija obavještenje sa obrazloženjem. Odluka zatvara predmet."
            : "Podnosilac dobija obavještenje i može izmijeniti podatke i priloge, pa ponovo poslati zahtjev.")}</Text>
          {dlg === "return" && badCount > 0 && (
            <Button size="compact-xs" variant="subtle" w="fit-content" onClick={() => setNote(items.filter((i) => cl[i]?.ok === false)
              .map((i) => `- ${i}${cl[i]?.note ? `: ${cl[i]?.note}` : ""}`).join("\n"))}>{t("Preuzmi neispunjene provjere u tekst")}</Button>
          )}
          <Textarea label={t(dlg === "reject" ? "Obrazloženje" : "Šta treba dopuniti")} required autosize minRows={4} value={note}
            onChange={(e) => setNote(e.currentTarget.value)} data-autofocus />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDlg(null)}>{t("Odustani")}</Button>
            <Button color={dlg === "reject" ? "red" : "orange"} disabled={!note.trim()} loading={busy} onClick={() => decide(dlg as "return" | "reject")}>
              {t(dlg === "reject" ? "Odbij zahtjev" : "Vrati na dopunu")}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={dlg === "approve"} onClose={() => setDlg(null)} centered radius="lg" size="lg"
        title={<Text fw={750}>{t("Odobravanje i aktiviranje gazdinstva")}</Text>}>
        <Stack gap="sm">
          <List spacing={8} size="sm" icon={<ThemeIcon size={22} radius="xl" color="green" variant="light"><IconCheck size={13} /></ThemeIcon>}>
            <List.Item>{reg?.holding
              ? t(`Postojeće gazdinstvo BPG ${reg.holding.bpg} („${reg.holding.name}“) biće označeno kao verifikovano i aktivno.`)
              : t(`Kreira se novo gazdinstvo BPG ${c.data.bpg ?? c.bpg} („${c.data.holding_name || c.data.holder_name || ""}“), status: verifikovano.`)}</List.Item>
            <List.Item>{t(`Voćnjaka koji se kreiraju na česticama iz katastra: ${orchardsToCreate} od ${c.data.orchards?.length ?? 0}.`)}</List.Item>
            <List.Item>{t(`Podnosilac (${c.applicant_name ?? c.applicant_username ?? ""}) dobija pristup podacima gazdinstva (BPG + uloga proizvođača) i obavještenje.`)}</List.Item>
          </List>
          {(badCount > 0 || okCount < items.length) && (
            <Alert color="orange" variant="light" icon={<IconAlertTriangle size={16} />}>
              {t(`Lista provjera nije kompletna: ${okCount}/${items.length} potvrđeno.`)}
            </Alert>
          )}
          {(reg?.parcels ?? []).some((p) => otherUse(p.cadastre?.koristi, c.data.bpg ?? c.bpg)) && (
            <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>{t("Neke čestice već koriste voćnjaci drugih gazdinstava.")}</Alert>
          )}
          <Textarea label={t("Napomena uz odluku (vidi je podnosilac)")} placeholder={t("Podaci su provjereni i ispravni.")} autosize minRows={2}
            value={note} onChange={(e) => setNote(e.currentTarget.value)} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDlg(null)}>{t("Odustani")}</Button>
            <Button color="agri" leftSection={<IconCircleCheck size={16} />} loading={busy} onClick={() => decide("approve")}>{t("Odobri")}</Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  );
}

function CheckItem({ caseId, item, state, disabled, onSaved }: {
  caseId: number; item: string; state?: CheckState; disabled: boolean; onSaved: () => void;
}) {
  const { t } = useT();
  const qc = useQueryClient();
  const [ok, setOk] = useState<boolean | null>(state?.ok ?? null);
  const [note, setNote] = useState(state?.note ?? "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setOk(state?.ok ?? null); setNote(state?.note ?? ""); }, [state?.ok, state?.note]);
  const save = async (nOk: boolean | null, nNote: string) => {
    setSaving(true);
    try {
      const r = await http.put<{ checklist: Record<string, CheckState> }>(`/api/v1/cases/${caseId}/checklist`, { item, ok: nOk, note: nNote || null });
      qc.setQueryData<CaseDetail>(["case", String(caseId)], (old) => old ? { ...old, checklist: r.checklist } : old);
      onSaved();
    } catch (e) {
      notifications.show({ color: "red", message: errMsg(e) });
    } finally { setSaving(false); }
  };
  const val = ok === true ? "da" : ok === false ? "ne" : "";
  const stateAttr = ok === true ? "ok" : ok === false ? "bad" : "none";
  const seg = useMemo(() => [
    { value: "", label: "—" },
    { value: "da", label: <IconCheck size={14} style={{ display: "block" }} /> },
    { value: "ne", label: <IconX size={14} style={{ display: "block" }} /> },
  ], []);
  return (
    <div className="case-check" data-state={stateAttr} style={{ padding: "6px 8px" }}>
      <Group justify="space-between" wrap="nowrap" gap="xs" align="flex-start">
        <Text size="sm" style={{ flex: 1 }} fw={500}>{item}</Text>
        <SegmentedControl size="xs" radius="xl" value={val} disabled={disabled || saving} data={seg}
          color={ok === true ? "green" : ok === false ? "red" : "gray"}
          onChange={(v) => { const n = v === "da" ? true : v === "ne" ? false : null; setOk(n); save(n, note); }} />
      </Group>
      {(ok === false || note || !disabled) && (
        <TextInput size="xs" mt={4} variant="filled" placeholder={t("Napomena (opciono)")} value={note} disabled={disabled}
          onChange={(e) => setNote(e.currentTarget.value)}
          onBlur={() => { if ((note || "") !== (state?.note ?? "")) save(ok, note); }}
          onKeyDown={(e) => e.key === "Enter" && (e.currentTarget as HTMLInputElement).blur()} />
      )}
      {state?.by && <Text size="10px" c="dimmed" mt={2}>{state.by} · {fmtDateTime(state.at)}</Text>}
    </div>
  );
}

// ============================================================================== komentar
function CommentBox({ caseId, onSent }: { caseId: number; onSent: () => void }) {
  const { t } = useT();
  const [note, setNote] = useState("");
  const [internal, setInternal] = useState(true);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      await http.post(`/api/v1/cases/${caseId}/comments`, { note: note.trim(), internal });
      setNote(""); onSent();
      notifications.show({ color: "green", message: t(internal ? "Interna napomena je sačuvana" : "Poruka je poslata podnosiocu") });
    } catch (e) { notifications.show({ color: "red", message: errMsg(e) }); } finally { setBusy(false); }
  };
  return (
    <Stack gap={6}>
      <Textarea placeholder={t(internal ? "Interna napomena (vide je samo zaposleni)..." : "Poruka podnosiocu (dobija obavještenje i e-poštu)...")}
        autosize minRows={2} maxRows={6} value={note} onChange={(e) => setNote(e.currentTarget.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) send(); }}
        styles={{ input: internal ? { background: "rgba(250,176,5,.07)" } : undefined }} />
      <Group justify="space-between">
        <Switch size="sm" checked={internal} onChange={(e) => setInternal(e.currentTarget.checked)} label={t("Interna napomena")}
          thumbIcon={internal ? <IconLock size={10} color="var(--mantine-color-gray-7)" /> : undefined} />
        <Button size="xs" leftSection={<IconSend size={14} />} disabled={!note.trim()} loading={busy} onClick={send}
          color={internal ? "gray" : "agri"}>{t(internal ? "Sačuvaj" : "Pošalji")}</Button>
      </Group>
    </Stack>
  );
}
